import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogTitle from '@mui/material/DialogTitle'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import CaleoSecondaryButton from '../Buttons/CaleoSecondaryButton'

/**
 * The extra props this HOC adds vary in shape depending on the wrapped modal, so they
 * are modelled as a loose record with the fields this file itself relies on made explicit.
 */
type ConfirmationModalProps = {
  open?: boolean
  onClose?(value?: unknown): void
  noEscClose?: boolean
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false
  fullWidth?: boolean
  noLabel?: string
  yesLabel?: string
  confirm?: () => void
  [key: string]: unknown
}

/**
 * Confirmation modal higher order component.
 *
 * @param ModalComponent - Modal component to wrap.
 * @returns Confirmation modal component.
 * @notExported
 */
const withConfirmationModal = <TModalProps extends object>(ModalComponent: React.ComponentType<TModalProps>) => {
  const ConfirmationModal = (props: ConfirmationModalProps) => {
    const { t } = useTranslation()
    const [disableDelete, setDisableDelete] = useState<boolean>(false)

    return (
      <>
        {props.open && (
          <Dialog
            open
            onClose={() => {
              if (props.noEscClose) {
                return
              }
              props.onClose?.({})
            }}
            maxWidth={props.maxWidth}
            fullWidth={props.fullWidth}
            aria-labelledby="with-confirmation-dialog-title"
          >
            <DialogTitle
              id="with-confirmation-dialog-title"
              sx={{
                position: 'absolute',
                width: 1,
                height: 1,
                overflow: 'hidden',
                clip: 'rect(0,0,0,0)',
                whiteSpace: 'nowrap',
              }}
            >
              {t('confirmation')}
            </DialogTitle>
            {React.createElement(ModalComponent, {
              ...props,
              disableDelete: setDisableDelete,
            } as unknown as React.Attributes & TModalProps)}
            <DialogActions
              sx={{ background: theme => theme.palette.background.paper, paddingRight: '22px', paddingBottom: '22px' }}
            >
              <CaleoSecondaryButton
                testId="button-confirmation-modal-no"
                label={props.noLabel ? t(props.noLabel) : !disableDelete ? t('confirmation.no') : t('confirmation.ok')}
                valid={true}
                clickAction={() => props.onClose?.()}
              />
              {!disableDelete && (
                <CaleoSecondaryButton
                  testId="button-confirmation-modal-yes"
                  label={props.yesLabel ? t(props.yesLabel) : t('confirmation.yes')}
                  valid={true}
                  clickAction={() => props.confirm?.()}
                />
              )}
            </DialogActions>
          </Dialog>
        )}
      </>
    )
  }
  return ConfirmationModal
}

export default withConfirmationModal
